import {
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    DialogActions,
    CircularProgress,
    Tooltip,
    IconButton,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormControl,
} from '@material-ui/core';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useLang } from '../../../../util/lang';
import { CaseInfoFile, PLATFORM_TYPE } from '../../../../model/oauth/type';
import moment from 'moment';
import { STL_OPTIONS, stlTypeOptions } from '../StlImpression_util';
import { OAuthFileUrlRespond, OAuthModalState, SelectedOAuthStlTypes } from '../../../../hooks/useOAuthConnectPlatform';
import useDebounce from '../../../../hooks/useDebounce';
import { useDispatch } from 'react-redux';
import { easySnackbar } from '../../../../store/Noti/noti.actions';
import Axios from 'axios';
import CaseInfo from '../../../../model/oauth/caseInfo';
import CachedIcon from '@material-ui/icons/Cached';

/**
 * @typedef {Object} DownloadedBlobFiles
 * @property {string} stlType
 * @property {string} fileName
 * @property {Blob} file
 */

/**
 * A modal component for connected platforms.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.open - Whether the modal is open.
 * @param {Function} props.onClose - The function to call when the modal is closed.
 * @param {boolean} props.loading - Whether the modal is loading.
 * @param {PLATFORM_TYPE} props.platform - The platform type.
 * @param {OAuthModalState} props.modal - The modal state.
 * @param {Function} props.setDownloadedBlobFiles - The function to set the downloaded blob files.
 * @param {Function} props.handleFileToImpression - The function to set downloaded blob files onto Impression Page refer to {.../patients/newPatient/FLEX}
 * @param {Function} props.reload - The function to reload the modal.
 * @param {Function} props.validateOAuthSelectNecessaryStl - The function to validate the selected necessary stl.
 * @param {Function} props.showMoreRecord - The async function to show more records.
 * @param {Function} props.searchCases - The async function to search cases.
 * @param {Function} props.setSelectedOAuthStlTypes - The function to set the selected OAuth stl types.
 * @param {[SelectedOAuthStlTypes]} props.selectedOAuthStlTypes - The selected OAuth stl types.
 * @param {(param:SelectedOAuthStlTypes[] , access_token :string , platform : PLATFORM_TYPE) => Promise<OAuthFileUrlRespond[]>} props.getOAuthFileDownloadUrl - The async function to get the OAuth platform file download URL.
 */
const ConnectedPlatformModal = ({
    open = false,
    onClose,
    platform,
    modal,
    setDownloadedBlobFiles,
    loading,
    handleFileToImpression,
    selectedOAuthStlTypes,
    setSelectedOAuthStlTypes,
    searchCases,
    showMoreRecord,
    validateOAuthSelectNecessaryStl,
    reload,
    getOAuthFileDownloadUrl,
    fileSlots,
}) => {
    const dispatch = useDispatch();
    const { caseInfos, title } = modal;
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 1500);
    const get = useLang('');
    const getPatientInfo = useLang('PatientInfo');
    const [downloadLoading, setDownloadLoading] = useState(false);

    const modalLoading = useMemo(() => {
        return loading || downloadLoading;
    }, [downloadLoading, loading]);

    const closeModal = () => {
        setSearchText('');
        setSelectedOAuthStlTypes([]);
        setDownloadedBlobFiles([]);
        onClose();
    };

    /**
     * @callback
     * @type {(e:Event , f:CaseInfoFile , caseInfo:CaseInfo) => void}
     */
    const handleSelectStlType = useCallback(
        (e, f, caseInfo) => {
            const { name, uuid } = f; // extract value from file object
            const selectedStlType = {
                stlType: e.target.value,
                fileId: uuid,
                fileName: name,
                caseId: caseInfo.uuid,
            };
            const selectedCaseIndex = selectedOAuthStlTypes.findIndex(
                (v) => v.caseId === caseInfo.uuid && v.fileId === uuid
            );
            // if the case is already selected, update the stlType
            if (selectedCaseIndex >= 0) {
                setSelectedOAuthStlTypes((draft) => {
                    draft[selectedCaseIndex] = selectedStlType;
                });
                return;
            }
            // if the case is not selected, add it to the list
            setSelectedOAuthStlTypes((draft) => {
                draft.push(selectedStlType);
            });
            return;
        },
        [selectedOAuthStlTypes]
    );

    /**
     * This function is for Eletra, only require one file
     * so set a new array with Upper type whenever user select a file
     * @callback
     * @type {(e:Event, f:CaseInfoFile, caseInfo:CaseInfo) => void}
     */
    const handleSelectRadio = (e, f, caseInfo) => {
        const { name, uuid } = f; // extract value from file object
        const selectedStlType = {
            stlType: STL_OPTIONS.upperFileName,
            fileId: uuid,
            fileName: name,
            caseId: caseInfo.uuid,
        };
        setSelectedOAuthStlTypes([selectedStlType]);
    };

    const renderShowMoreRecordBtn = useMemo(() => {
        if (!modal || !modal.pageInfo) return false;
        return modal.pageInfo.totalElements > modal.pageInfo.numberOfElements;
    }, [modal.pageInfo]);

    const disableGetMoreRecord = useMemo(() => {
        if (modal.platform === PLATFORM_TYPE.MeditLink) {
            const currentPage = modal.pageInfo.page + 1;
            return currentPage >= modal.pageInfo.totalPage;
        }
        return modal.pageInfo.page >= modal.pageInfo.totalPage;
    }, [modal.pageInfo, modal.platform]);

    const disabledAutoFillIn = useMemo(() => {
        return !selectedOAuthStlTypes.length;
    }, [selectedOAuthStlTypes]);

    const isEletra = useMemo(() => {
        return platform === PLATFORM_TYPE.PandaCloud_EN || platform === PLATFORM_TYPE.PandaCloud_EU;
    }, [modal.platform]);

    /**
     * @callback
     * @type {(caseInfo:CaseInfo , fileName:string , platform:PLATFORM_TYPE) => void}
     */
    const getCaseDisplayName = useCallback((caseInfo, fileName, platform) => {
        switch (platform) {
            case PLATFORM_TYPE.MeditLink:
                return `${caseInfo.name} - ${fileName}`;
            default:
                return fileName;
        }
    }, []);

    // const caseDisplayTitle = useMemo(() => {
    //     switch (platform) {
    //         case PLATFORM_TYPE.MeditLink:
    //             return `Case Name - File Name`
    //         default:
    //             return 'Case Name'
    //     }
    // },[platform])

    useEffect(() => {
        if (debouncedSearchText.length > 0 && searchCases) {
            searchCases(debouncedSearchText, modal);
        }
    }, [debouncedSearchText, searchCases]);

    /** Maps a downloaded STL type to the active section's file slot. */
    const getFileType = useCallback((stlType) => {
        switch (stlType) {
            case STL_OPTIONS.upperFileName:
                return fileSlots?.upper || 'impression_upper';
            case STL_OPTIONS.lowerFileName:
                return fileSlots?.lower || 'impression_lower';
            case STL_OPTIONS.biteOneFileName:
                return fileSlots?.bite || 'impression_scan_one';
            case STL_OPTIONS.biteTwoFileName:
                return fileSlots?.biteTwo || 'impression_scan_two';
            default:
                return '';
        }
    }, [fileSlots]);

    /**
     * @callback
     * @type {(respond : [OAuthFileUrlRespond] , selectedOAuthStlTypes : [SelectedOAuthStlTypes]) => Promise<void>}
     */
    const downloadFileToImpression = useCallback(
        async (respond, selectedOAuthStlTypes) => {
            try {
                if (!selectedOAuthStlTypes || !selectedOAuthStlTypes.length) throw new Error('No file selected');
                // match the file with the selected stlType, return a list with url
                const stlTypes = selectedOAuthStlTypes.map((value) => {
                    const found = respond.find((r) => r.uuid === value.fileId);
                    if (found) {
                        return {
                            ...value,
                            fileName: found.downloadFileName,
                            url: found.url,
                        };
                    }
                    return value;
                });
                // download the file
                const promise = await Promise.allSettled(
                    stlTypes.map(async (value) => {
                        const res = await Axios.get(value.url, { responseType: 'blob' });
                        if (res.data && res.status === 200) {
                            return {
                                stlType: value.stlType,
                                fileName: value.fileName,
                                file: res.data,
                            };
                        }
                    })
                );

                if (promise.some((v) => v.status !== 'fulfilled') || !promise.length)
                    throw new Error('Failed download files');
                /**
                 * @type {DownloadedBlobFiles[]}
                 */
                // get the blob files and set it to impression page
                const blobFiles = promise.map((v) => v.value);
                blobFiles.forEach((value) => {
                    const matchType = stlTypeOptions.find((v) => v.value === value.stlType);
                    if (matchType) {
                        const file = new File([value.file], value.fileName);
                        handleFileToImpression(getFileType(value.stlType), file, modal.platform);
                    }
                });
                closeModal();
            } catch (error) {
                console.error(error);
                throw new Error('Failed to download files');
            }
        },
        [handleFileToImpression, getFileType, Axios, closeModal]
    );

    const handleDownloadSelectedStl = useCallback(async () => {
        if (!handleFileToImpression) throw new Error('function handleFileToImpression is not defined');
        try {
            setDownloadLoading(true);
            // validate the selected stlTypes, must include upper , lower and biteOne
            validateOAuthSelectNecessaryStl(modal.platform);
            const isPlatfromSupport = [
                PLATFORM_TYPE.MeditLink,
                PLATFORM_TYPE.PandaCloud_EN,
                PLATFORM_TYPE.PandaCloud_EU,
            ].includes(modal.platform);
            if (!isPlatfromSupport) throw new Error('Platform not supported');
            // get file url
            const res = await getOAuthFileDownloadUrl(selectedOAuthStlTypes, modal.access_token, modal.platform);
            if (!res || !res.length) throw new Error('No file to download');
            // handle downloadfile to impression page
            await downloadFileToImpression(res, selectedOAuthStlTypes);
        } catch (error) {
            dispatch(easySnackbar(error.message, 'error'));
        } finally {
            setDownloadLoading(false);
        }
    }, [selectedOAuthStlTypes, modal.platform, dispatch, validateOAuthSelectNecessaryStl, getOAuthFileDownloadUrl]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            // style={{pointerEvents : modalLoading ? 'none' : 'auto'}}
        >
            {modalLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100,
                    }}
                    // onClick={(e) => e.stopPropagation()}
                >
                    <CircularProgress color="secondary" size={60} />
                </div>
            )}
            <DialogTitle>
                {title}
                <Tooltip title="Refresh">
                    <IconButton
                        onClick={() => {
                            setSearchText('');
                            reload();
                        }}
                        disabled={modalLoading}
                    >
                        <CachedIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>
            <DialogContent>
                <Grid item xs={12} style={{ marginBottom: 20 }}>
                    <TextField
                        id="search-text"
                        label={getPatientInfo('OAuth.Modal.Search')}
                        fullWidth
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        disabled={modalLoading}
                    />
                </Grid>

                <Grid container style={{ overflowWrap: 'break-word', position: 'relative' }}>
                    <Grid item xs={2} style={{ border: '1px solid grey' }}>
                        {getPatientInfo('OAuth.Modal.Status')}
                    </Grid>
                    <Grid item xs={2} style={{ border: '1px solid grey' }}>
                        {getPatientInfo('OAuth.Modal.PatientName')}
                    </Grid>
                    <Grid item xs={8}>
                        <Grid container>
                            <Grid item xs={7} style={{ border: '1px solid grey' }}>
                                {/* {caseDisplayTitle} */}
                                {getPatientInfo('OAuth.Modal.CaseName')}
                            </Grid>
                            <Grid item xs={3} style={{ border: '1px solid grey' }}>
                                {getPatientInfo('OAuth.Modal.CreatedDate')}
                            </Grid>
                            <Grid item xs={2} style={{ border: '1px solid grey' }}>
                                {getPatientInfo('OAuth.Modal.Select')}{' '}
                                {modal.platform === PLATFORM_TYPE.MeditLink ? 'Stl Type' : 'File'}
                            </Grid>
                        </Grid>
                    </Grid>

                    {caseInfos.length > 0 &&
                        caseInfos.map((value, i) => {
                            const caseInfo = value;
                            if (!caseInfo) {
                                return (
                                    <Grid item xs={12} key={i}>
                                        Error Occured
                                    </Grid>
                                );
                            }
                            return (
                                <>
                                    <Grid item xs={2} style={{ border: '1px solid grey' }} key={value.uuid | i}>
                                        {caseInfo.status}
                                    </Grid>
                                    <Grid item xs={2} style={{ border: '1px solid grey' }}>
                                        {caseInfo.patient.name}
                                    </Grid>
                                    <Grid item xs={8}>
                                        {caseInfo.files.map((f) => {
                                            return (
                                                <Grid container key={f.uuid}>
                                                    <Grid item xs={7} style={{ border: '1px solid grey' }}>
                                                        {getCaseDisplayName(caseInfo, f.name, platform)}
                                                    </Grid>
                                                    <Grid item xs={3} style={{ border: '1px solid grey' }}>
                                                        {f.dateCreated
                                                            ? moment(f.dateCreated).format('YYYY-MM-DD HH:mm')
                                                            : ''}
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={2}
                                                        style={{
                                                            border: '1px solid grey',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: '10px',
                                                        }}
                                                    >
                                                        {isEletra ? (
                                                            <Radio
                                                                checked={selectedOAuthStlTypes.some(
                                                                    (v) => v.fileId === f.uuid
                                                                )}
                                                                name="caseInfoRadioGroup"
                                                                inputProps={{ 'aria-label': 'A' }}
                                                                value={f.uuid}
                                                                onChange={(e) => handleSelectRadio(e, f, value)}
                                                            />
                                                        ) : (
                                                            <select onChange={(e) => handleSelectStlType(e, f, value)}>
                                                                {stlTypeOptions.map((stlType, i) => (
                                                                    <option
                                                                        key={f.uuid + i}
                                                                        value={stlType.value}
                                                                        disabled={selectedOAuthStlTypes.some(
                                                                            (v) => v.stlType === stlType.value
                                                                        )}
                                                                    >
                                                                        {stlType.text}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </>
                            );
                        })}

                    <Grid item xs={9}>
                        {renderShowMoreRecordBtn && (
                            <Button
                                variant="outlined"
                                onClick={showMoreRecord}
                                disabled={modalLoading || disableGetMoreRecord}
                            >
                                Show more records
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Grid item xs={3} style={{ textAlign: 'right' }}>
                    <Button disabled={modalLoading || disabledAutoFillIn} onClick={handleDownloadSelectedStl}>
                        {getPatientInfo('OAuth.Modal.SelectedAndAutoFillIn')} <GetAppIcon />
                    </Button>
                </Grid>
                <Button onClick={closeModal} disabled={modalLoading}>
                    {get(':const.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default memo(ConnectedPlatformModal);
